import { faPen, faTrash } from '@fortawesome/pro-regular-svg-icons';
import {
    faPen as faPenSolid,
    faTrash as faTrashSolid,
} from '@fortawesome/pro-solid-svg-icons';
import {
    AssetCategoryParameterType,
    IAssetCategoryParameterResponse,
    IAssetCategoryResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { useEffectSkipFirst } from 'custom_hooks/useEffectSkipFirst';
import {
    AssetParameterEditInfo,
    EditAssetParameterModal,
} from 'pages/Settings/AssetType/EditAssetParameterModal';
import {
    TreeItemActions,
    TreeItemBar,
    TreeItemLabel,
} from 'pages/Settings/AssetType/styled';
import { useState } from 'react';
import { DataTree } from 'shared/data_tree/DataTree';
import { createTree } from 'shared/data_tree/tree';
import { TreeNodeData } from 'shared/data_tree/types';
import { DebugValue } from 'shared/debug/DebugValue';
import { tokens } from 'styles/tokens';
import { excludeNullAndUndefined, sortBy, toggleArrayItem } from 'utils/array';
import {
    deleteAssetParameter,
    formattedParameterTypes,
} from 'utils/asset_classification';
import { Lookup } from 'utils/types';

export type ParamTreeData = Readonly<{
    label?: string;
    value: string;
    parameter?: IAssetCategoryParameterResponse;
}>;

const defaultDataLookup: Lookup<
    AssetCategoryParameterType,
    (value: string) => ParamTreeData
> = {
    [AssetCategoryParameterType.Text]: (value) => ({ label: 'Default Value', value }),
    [AssetCategoryParameterType.Integer]: (value) => ({ label: 'Default Value', value }),
    [AssetCategoryParameterType.Decimal]: (value) => ({ label: 'Default Value', value }),
    [AssetCategoryParameterType.MonitoringPointId]: (value) => ({
        label: 'Default Value',
        value,
    }),
    [AssetCategoryParameterType.Selection]: (value) => ({
        label: 'Default Selection',
        value,
    }),
    [AssetCategoryParameterType.Bool]: (value) => ({
        label: 'Default Status',
        value: value === 'true' ? 'Yes' : 'No',
    }),
};

const formatNumber = (value: number, isInclusive: boolean): string =>
    isInclusive ? `${value} (inclusive)` : `${value}`;

const buildParamTreeData = (params: IAssetCategoryParameterResponse[]) =>
    params.map<TreeNodeData<ParamTreeData>>((param) => {
        const paramKey = `${param.Category.AssetCategoryId}.param.${param.Code}`;

        return {
            id: paramKey,
            data: {
                value: param.Name,
                parameter: param,
            },
            children: excludeNullAndUndefined<TreeNodeData<ParamTreeData>>([
                {
                    id: `${paramKey}.code`,
                    data: { label: 'Code', value: param.Code },
                },
                {
                    id: `${paramKey}.type`,
                    data: {
                        label: 'Type',
                        value: formattedParameterTypes[param.CategoryParameterType],
                    },
                },
                param.MinValue !== undefined
                    ? {
                          id: `${paramKey}.min-value`,
                          data: {
                              label: 'Min Value',
                              value: formatNumber(
                                  param.MinValue,
                                  param.MinValueInclusive ?? false
                              ),
                          },
                      }
                    : null,
                param.MaxValue !== undefined
                    ? {
                          id: `${paramKey}.max-value`,
                          data: {
                              label: 'Max Value',
                              value: formatNumber(
                                  param.MaxValue,
                                  param.MaxValueInclusive ?? false
                              ),
                          },
                      }
                    : null,
                param.ValidValues !== undefined && param.ValidValues.length > 0
                    ? {
                          id: `${paramKey}.options`,
                          data: { value: 'Options' },
                          children: sortBy(param.ValidValues, (val) => val).map<
                              TreeNodeData<ParamTreeData>
                          >((validValue) => ({
                              id: `${paramKey}.options.${validValue}`,
                              data: { value: validValue },
                          })),
                      }
                    : null,
                param.DefaultValue
                    ? {
                          id: `${paramKey}.default-value`,
                          data: defaultDataLookup[param.CategoryParameterType](
                              param.DefaultValue
                          ),
                      }
                    : null,
            ]),
        };
    });

export type AssetParameterDetailTreeProps = Readonly<{
    category: IAssetCategoryResponse;
    parameters: IAssetCategoryParameterResponse[];
    onChange: () => void;
}>;

export function AssetParameterDetailTree({
    category,
    parameters,
    onChange,
}: AssetParameterDetailTreeProps) {
    const { showToast } = useNotificationContext();
    const { canEditAssetType: canEditAssetParameter } = usePermissions();
    const [parameterEditInfo, setParameterEditInfo] = useState<AssetParameterEditInfo>();

    const [
        assetParameterDeleteInfo,
        setAssetParameterDeleteInfo,
    ] = useState<IAssetCategoryParameterResponse>();

    const { mutate: deleteParameter, isPending: isDeleting } = useMutation({
        mutationFn: async (update: { categoryId: number; parameterCode: string }) =>
            deleteAssetParameter(update.categoryId, update.parameterCode),

        onSuccess: () => {
            setAssetParameterDeleteInfo(undefined);
            onChange();
            showToast({
                type: 'success',
                content: 'Successfully deleted asset category parameter',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to delete asset category parameter',
            }),
    });

    const [expandedParameterIds, setExpandedParameterIds] = useState<string[]>([]);

    const [tree, setTree] = useState(createTree(buildParamTreeData(parameters)));

    const onEditParameterClick = (
        parameter: IAssetCategoryParameterResponse | undefined
    ) => {
        if (parameter) {
            setParameterEditInfo({
                parameter,
                siblingParameters: parameters.filter(
                    (param) => param.Code !== parameter.Code
                ),
                category,
            });
        }
    };

    const onDeleteParameterClick = (
        parameter: IAssetCategoryParameterResponse | undefined
    ) => {
        if (parameter) {
            setAssetParameterDeleteInfo(parameter);
        }
    };

    const onEditParameterClose = (saved: boolean) => {
        setParameterEditInfo(undefined);
        if (saved) {
            onChange();
        }
    };

    useEffectSkipFirst(() => {
        setTree(createTree(buildParamTreeData(parameters)));
    }, [setTree, parameters]);

    return (
        <>
            {parameters.length === 0 && <div>No Parameters</div>}
            <DataTree
                treeId="asset-category-parameters"
                data={tree}
                renderNode={({ node }) => (
                    <TreeItemBar className="f-14">
                        <TreeItemLabel>
                            {node.data.label && (
                                <div
                                    className="bold"
                                    title={node.data.label}
                                >{`${node.data.label}: `}</div>
                            )}
                            <div>{node.data.value}</div>
                            {node.data.parameter && (
                                <DebugValue
                                    value={node.data.parameter}
                                    description="Asset Category Parameter"
                                />
                            )}
                        </TreeItemLabel>
                        {canEditAssetParameter && node.data.parameter !== undefined && (
                            <TreeItemActions>
                                <HoverIconButton
                                    icon={faPen}
                                    hoverIcon={faPenSolid}
                                    color={tokens.icon.actionActive}
                                    hoverColor={tokens.icon.actionHover}
                                    size="14"
                                    onClick={() =>
                                        onEditParameterClick(node.data.parameter)
                                    }
                                />
                                <HoverIconButton
                                    icon={faTrash}
                                    hoverIcon={faTrashSolid}
                                    color={tokens.icon.actionActive}
                                    hoverColor={tokens.error.default}
                                    size="14"
                                    onClick={() =>
                                        onDeleteParameterClick(node.data.parameter)
                                    }
                                />
                            </TreeItemActions>
                        )}
                    </TreeItemBar>
                )}
                expandable={{
                    canExpand: (node) => node.children !== undefined,
                    isExpanded: (node) => expandedParameterIds.includes(node.id),
                    onChange: (event) =>
                        setExpandedParameterIds((current) =>
                            toggleArrayItem(current, event.node.id)
                        ),
                }}
            />
            {parameterEditInfo && (
                <EditAssetParameterModal
                    {...parameterEditInfo}
                    onClose={onEditParameterClose}
                />
            )}
            {assetParameterDeleteInfo && (
                <ConfirmModal
                    title={`Delete '${assetParameterDeleteInfo.Name}'`}
                    actionText="delete this parameter? Other users in your organization may be referencing it."
                    checkText="delete this parameter"
                    actionButtonText="Delete"
                    closable={!isDeleting}
                    actionable={!isDeleting}
                    onAction={() =>
                        deleteParameter({
                            categoryId: assetParameterDeleteInfo.Category.AssetCategoryId,
                            parameterCode: assetParameterDeleteInfo.Code,
                        })
                    }
                    onClose={() => setAssetParameterDeleteInfo(undefined)}
                />
            )}
        </>
    );
}
